import type {Command, CompletionUserMessage, LogEntry} from '../types/session'
import {isCompletionUserMessage} from '../types/session'

const NON_USER_FACING_SOURCE_PREFIXES: readonly string[] = ['skill-', 'instruction-discovery', 'system']

/**
 * Returns true if the source indicates an injected runtime message
 * (e.g. skill context) that should not be rendered as user-visible content.
 */
export function isInjectedSource(source: string | undefined): boolean {
  return NON_USER_FACING_SOURCE_PREFIXES.some(prefix => source?.startsWith(prefix))
}

/**
 * Returns true if the message was injected by the runtime (e.g. skill context)
 * and should not be rendered as user-visible content.
 */
export function isInjectedMessage(message: CompletionUserMessage): boolean {
  return isInjectedSource(message.source)
}

/**
 * Checks whether a log entry's source matches a command, using the MC command
 * id as the primary key and falling back to external_id for existing
 * CCA/sweagentd sessions.
 */
export function isSourceForCommand(source: string | undefined, command: Command): boolean {
  return (
    source === `command-${command.id}` || (command.external_id != null && source === `command-${command.external_id}`)
  )
}

/**
 * Returns the text that should be shown to the user for a command.
 *
 * Prefers `display_content` — a short human-readable summary supplied by the
 * producer of the command — falling back to the raw `content` when it is
 * absent or blank. This mirrors the server-side behaviour in Mission Control,
 * so a command rendered optimistically (while queued/in progress) reads the
 * same as the event that later echoes it back.
 */
export function commandDisplayContent(command: Command): string {
  const display = command.display_content
  if (display != null && display.trim() !== '') {
    return display
  }
  return command.content
}

/**
 * Checks if a command appears in the logs by matching the source field.
 */
export function isCommandInLogs(command: Command, logEntry: LogEntry): boolean {
  if (!isCompletionUserMessage(logEntry)) return false
  return isSourceForCommand(logEntry.source, command)
}

/**
 * Filters commands to return only those that should still be shown as pending
 * (not yet visible in the log stream).
 *
 * A command is hidden when:
 * - It appears in the logs (matched by source), OR
 * - It was dismissed (session ended before processing)
 *
 * Failed commands are always shown (they never appear in logs).
 */
export function getPendingCommands(commandsData: Command[] | undefined, logsData: LogEntry[] | undefined): Command[] {
  if (!commandsData?.length) {
    return []
  }

  return commandsData.filter(command => {
    if (command.state === 'dismissed') {
      return false
    }

    if (command.state === 'failed') {
      return true
    }

    // Hide the command once it appears in the log stream
    if (logsData?.some(log => isCommandInLogs(command, log))) {
      return false
    }

    return true
  })
}
